"use client";

import { CMSLink } from "@/Link";
import type { ResolvedCMSLink } from "@/blocks/resolve";
import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import Image from "next/image";
import RichText from "@/components/RichText";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import { cn } from "@/utilities/ui";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";
import { mediaObjectFitClassName } from "@/utilities/mediaObjectFit";
import {
	leftRightColImageSizes,
	leftRightInsetImageSizes,
} from "@/utilities/leftRightImageSizes";

interface LeftRightBaseProps {
	sectionId?: string;
	title?: string;
	subtitle?: string;
	titleTag?: "h1" | "h2" | "h3";
	titleClassName?: string;
	body?: string | DefaultTypedEditorState;
	bodyAlignment?: "top" | "middle" | "bottom";
	points?: string[];
	links?: ResolvedCMSLink[];
	noPadding?: boolean;
	imagePosition?: "left" | "right";
	mediaType?: "image" | "iframe";
	imageAlt?: string;
	imageTitle?: string;
	imageObjectFit?: MediaObjectFit;
	backgroundImageTitle?: string;
	backgroundImageObjectFit?: MediaObjectFit;
	iframeUrl?: string;
	iframeTitle?: string;
}

type LeftRightImageModeProps =
	| (LeftRightBaseProps & {
			mediaType?: "image";
			imageSrc: string;
			backgroundImageSrc?: string;
	  })
	| (LeftRightBaseProps & {
			mediaType?: "image";
			imageSrc?: string;
			backgroundImageSrc: string;
	  });

type LeftRightIframeModeProps = LeftRightBaseProps & {
	mediaType: "iframe";
	imageSrc?: string;
	backgroundImageSrc?: string;
};

type LeftRightProps = LeftRightImageModeProps | LeftRightIframeModeProps;

export function LeftRight({
	sectionId,
	title,
	subtitle,
	titleTag = "h3",
	titleClassName = "text-xl",
	body,
	bodyAlignment = "top",
	points,
	noPadding = false,
	links,
	imagePosition = "right",
	mediaType = "image",
	imageSrc,
	imageAlt,
	imageTitle,
	imageObjectFit = "cover",
	backgroundImageSrc,
	backgroundImageTitle,
	backgroundImageObjectFit = "cover",
	iframeUrl,
	iframeTitle = "Embedded content",
}: LeftRightProps) {
	const showIframe = mediaType === "iframe" && Boolean(iframeUrl);
	const TitleTag = titleTag;
	const textOrderClass =
		imagePosition === "left"
			? "order-1 lg:order-2 lg:pl-2 lg:pr-6"
			: "order-1 lg:px-2";
	const imageOrderClass =
		imagePosition === "left" ? "order-2 lg:order-1" : "order-2";
	const hasBothImages = Boolean(imageSrc && backgroundImageSrc);
	const imageInsetClass = hasBothImages ? "" : imageSrc ? "lg:p-6" : "p-0";
	const bodyAlignmentClass =
		bodyAlignment === "middle"
			? noPadding
				? "justify-center"
				: "lg:justify-center"
			: bodyAlignment === "bottom"
				? noPadding
					? "justify-end"
					: "lg:justify-end"
				: noPadding
					? "justify-start"
					: "lg:justify-start";

	const textBlock = (
		<div
			className={cn(
				"flex flex-col justify-between w-full lg:w-5/12",
				noPadding ? "self-stretch" : "lg:self-stretch",
				textOrderClass,
				bodyAlignment !== "bottom" ? "gap-8" : "",
			)}
		>
			<div
				className={cn(
					"flex flex-col",
					noPadding ? "flex-1" : "lg:flex-1",
					bodyAlignmentClass,
				)}
			>
				<div className="flex flex-col gap-4 pt-2 pb-6">
					{title || subtitle ? (
						<div className="flex flex-col">
							{title ? (
								<TitleTag
									className={`${titleClassName} text-foreground text-pretty`}
								>
									{title}
								</TitleTag>
							) : null}
							{subtitle ? (
								<p
									className={`${titleClassName} text-muted-foreground text-pretty whitespace-pre-line`}
								>
									{subtitle}
								</p>
							) : null}
						</div>
					) : null}
					{typeof body === "string" ? (
						<p className="text-muted-foreground text-base text-pretty">
							{body}
						</p>
					) : body ? (
						<RichText
							data={body}
							enableGutter={false}
							enableProse={false}
							className="text-muted-foreground text-base text-pretty"
						/>
					) : null}
				</div>
				{points?.length ? (
					<ul className="text-muted-foreground list-disc space-y-2 pl-5 text-balance text-base">
						{points.map((point) => (
							<li key={point}>{point}</li>
						))}
					</ul>
				) : null}
			</div>

			{links?.length ? (
				<div className="flex flex-col items-start gap-3">
					{links?.map((link, index) => (
						<div
							key={`${link.label ?? "link"}-${index}`}
							className={index === links.length - 1 ? "pb-2" : ""}
						>
							<CMSLink
								{...link}
								appearance={link.appearance ?? "underline"}
								size="lg"
								className="group"
							>
								<ArrowRight className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1" />
							</CMSLink>
						</div>
					))}
				</div>
			) : null}
		</div>
	);

	const mediaBlock = (
		<div className={`w-full lg:w-7/12 ${imageOrderClass}`}>
			{showIframe ? (
				<div className="relative overflow-hidden rounded-sm md:rounded-xs">
					<iframe
						src={iframeUrl}
						loading="lazy"
						title={iframeTitle}
						allow="clipboard-write"
						frameBorder={0}
						style={{
							position: "relative",
							width: "100%",
							height: "100%",
							aspectRatio: "16/9",
							border: "none",
						}}
					/>
				</div>
			) : (
				<div
					className={`relative overflow-hidden rounded-sm md:rounded-xs ${imageInsetClass}`}
				>
					{hasBothImages && backgroundImageSrc ? (
						<Image
							src={backgroundImageSrc}
							alt=""
							fill
							sizes={leftRightColImageSizes}
							aria-hidden
							className={cn(
								"z-0",
								mediaObjectFitClassName(backgroundImageObjectFit),
							)}
						/>
					) : null}
					<div
						className={hasBothImages ? "relative z-10 p-6" : "relative z-10"}
					>
						<AspectRatio ratio={4 / 3} className="relative">
							{imageSrc ? (
								<Image
									src={imageSrc}
									alt={imageAlt ?? ""}
									title={imageTitle ?? imageAlt ?? undefined}
									fill
									sizes={
										hasBothImages
											? leftRightInsetImageSizes
											: leftRightColImageSizes
									}
									className={cn(
										"rounded-sm md:rounded-xs",
										mediaObjectFitClassName(imageObjectFit),
									)}
								/>
							) : backgroundImageSrc ? (
								<Image
									src={backgroundImageSrc}
									alt=""
									fill
									sizes={leftRightColImageSizes}
									aria-hidden
									className={cn(
										"rounded-sm md:rounded-xs",
										mediaObjectFitClassName(backgroundImageObjectFit),
									)}
								/>
							) : null}
						</AspectRatio>
					</div>
				</div>
			)}
		</div>
	);

	return (
		<section
			id={sectionId}
			className={`bg-background ${noPadding ? "" : "container container-padding-x section-padding-y"}`}
		>
			<div
				className={cn(
					noPadding &&
						"max-md:gap-4 flex-col-reverse items-stretch max-md:justify-start justify-end",
					"container-padding-x py-6 container mx-auto flex flex-col gap-12 rounded-xs md:rounded-md bg-[#f1f0ee] lg:flex-row lg:items-stretch lg:gap-6",
					!noPadding && "items-center",
				)}
			>
				{textBlock}
				{mediaBlock}
			</div>
		</section>
	);
}
